"use client";

import { useCallback, useEffect, useState } from "react";

/** Volume restored when unmuting a clip that was dragged to silence. */
const UNMUTE_VOLUME = 0.6;

export interface VideoTransport {
  /** Callback ref for the player: the hook needs the element, not a box. */
  attach: (video: HTMLVideoElement | null) => void;
  video: HTMLVideoElement | null;
  playing: boolean;
  /** A frame of the film has been on screen since the clip last ended. */
  started: boolean;
  elapsed: number;
  duration: number;
  volume: number;
  muted: boolean;
  togglePlay: () => void;
  /** Seeks and plays: every caller is a chapter or a scrubber. */
  seekTo: (seconds: number) => void;
  seekBy: (delta: number) => void;
  scrub: (seconds: number) => void;
  changeVolume: (volume: number) => void;
  toggleMute: () => void;
}

/**
 * Mirrors a media element's transport state into React and exposes the actions
 * the drawn controls need. Both story players use it, so neither has to re-wire
 * five media events, and the two cannot drift apart. The clip is a YouTube
 * embed behind an element that speaks the `HTMLVideoElement` API, so nothing
 * here has to know that.
 *
 * The element arrives through a callback ref rather than a RefObject because the
 * subscription has to re-run when the video mounts, and a ref's `.current` is
 * not reactive.
 */
export function useVideoTransport(): VideoTransport {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    if (!video) {
      return;
    }

    const syncPlayback = () => {
      setPlaying(!video.paused);
    };
    const syncDuration = () => {
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    };
    const syncTime = () => {
      setElapsed(video.currentTime);
      // The embed reports no duration until its own metadata lands, which can
      // be after it declares itself ready.
      syncDuration();
    };
    const syncVolume = () => {
      setVolume(video.volume);
      setMuted(video.muted);
    };
    // Not `play`, which the embed also fires while it is still buffering: the
    // poster stays over the frame until a frame of the film replaces it.
    const onPlaying = () => {
      setStarted(true);
    };
    // The poster goes back over the frame at the end, where YouTube draws its
    // related-video screen. Pressing play from there restarts the clip.
    const onEnded = () => {
      setStarted(false);
    };
    // Until the player is ready, `paused` answers from the autoplay attribute,
    // which is a request the browser can still refuse.
    const onReady = () => {
      syncDuration();
      syncPlayback();
    };

    video.addEventListener("play", syncPlayback);
    video.addEventListener("pause", syncPlayback);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("ended", onEnded);
    video.addEventListener("timeupdate", syncTime);
    video.addEventListener("loadedmetadata", onReady);
    video.addEventListener("durationchange", syncDuration);
    video.addEventListener("volumechange", syncVolume);

    // Seed from the element: with a cached clip, metadata can already be in
    // place by the time this effect runs.
    syncPlayback();
    syncTime();
    syncDuration();
    syncVolume();

    return () => {
      video.removeEventListener("play", syncPlayback);
      video.removeEventListener("pause", syncPlayback);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("timeupdate", syncTime);
      video.removeEventListener("loadedmetadata", onReady);
      video.removeEventListener("durationchange", syncDuration);
      video.removeEventListener("volumechange", syncVolume);
    };
  }, [video]);

  const togglePlay = useCallback(() => {
    if (!video) {
      return;
    }
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }, [video]);

  const scrub = useCallback(
    (seconds: number) => {
      if (!video) {
        return;
      }
      video.currentTime = seconds;
    },
    [video],
  );

  const seekTo = useCallback(
    (seconds: number) => {
      if (!video) {
        return;
      }
      video.currentTime = seconds;
      void video.play();
    },
    [video],
  );

  const seekBy = useCallback(
    (delta: number) => {
      if (!video) {
        return;
      }
      video.currentTime = Math.min(
        Math.max(video.currentTime + delta, 0),
        video.duration || 0,
      );
    },
    [video],
  );

  const changeVolume = useCallback(
    (next: number) => {
      if (!video) {
        return;
      }
      video.volume = next;
      // Dragging the rocker to zero is a mute, and dragging back off it is not.
      video.muted = next === 0;
    },
    [video],
  );

  const toggleMute = useCallback(() => {
    if (!video) {
      return;
    }
    const next = !video.muted;
    video.muted = next;
    if (!next && video.volume === 0) {
      video.volume = UNMUTE_VOLUME;
    }
  }, [video]);

  return {
    attach: setVideo,
    video,
    playing,
    started,
    elapsed,
    duration,
    volume,
    muted,
    togglePlay,
    seekTo,
    seekBy,
    scrub,
    changeVolume,
    toggleMute,
  };
}
